import {
  Avatar,
  dateFormatterDayMonthYearLong2Digits,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  sortByDateTime,
  Spacer,
  Switch,
  triggerToast,
  triggerToastDS,
  usePersistMuiTableState,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  managersListAsync,
  selectAddCompanyToCompaniesAccessStatus,
  selectCancelPasswordCreationRequestStatus,
  selectDeactivateManagerStatus,
  selectManagersList,
  selectManagersListStatus,
  selectRelaunchPasswordCreationRequestStatus,
  selectUpdateUserRoleStatus,
  updateUserMileageGapReportsAsync,
  UsersSlice,
} from "./usersSlice";
import { ManagerDataDisplay, ManagerDataDisplayRow } from "./usersAPI";
import { styled } from "styled-components";
import { TextCapitalized } from "@qivia/ui/src/designSystem/components/text/TextCapitalized";
import {
  selectCompany,
  selectLoadCompanyStatus,
  selectManager,
} from "../homeSlice";
import "../../../i18n/i18n";
import { GridValidRowModel, useGridApiRef } from "@mui/x-data-grid-premium";
import { documentDownloadXlsxAsync } from "../../../libSlice/downloadFileXslx";
import { StatusColorDict } from "./userStatus";
import { UserManagementSidePanel } from "./sidePanel/userManagementSidePanel";

export const UserManagementTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const navigate = useNavigate();

  const company = useAppSelector(selectCompany);
  const currentManager = useAppSelector(selectManager);
  const loadCompanyStatus = useAppSelector(selectLoadCompanyStatus);
  const managersListInit = useAppSelector(selectManagersList);
  const managersListStatus = useAppSelector(selectManagersListStatus);
  const cancelPasswordCreationRequestStatus = useAppSelector(
    selectCancelPasswordCreationRequestStatus,
  );
  const relaunchManagerStatus = useAppSelector(
    selectRelaunchPasswordCreationRequestStatus,
  );
  const updateUserRole = useAppSelector(selectUpdateUserRoleStatus);
  const deactivateManagerStatus = useAppSelector(selectDeactivateManagerStatus);
  const addCompanyToCompaniesAccessStatus = useAppSelector(
    selectAddCompanyToCompaniesAccessStatus
  );
  const [listDisplayed, setListDisplayed] = useState<
    ManagerDataDisplay[] | null
  >(null);

  const pageName = "users.management";

  const apiRef = useGridApiRef();
  const key = "customers-grid";
  const aggregation = {
    model: {
      name: "size",
      vehicleType: "size",
      status: "size",
      registrationNumber: "size",
      vehicleReference: "size",
      cardLast4Digits: "size",
      averagePricePerLiter: "avg",
      averageQuantity: "avg",
      averageMonthlyAmount: "avg",
      averageConsumption: "avg",
      averageCarbonEmissionPerKm: "avg",
      analyticCode1: "size",
      analyticCode2: "size",
      transactionsWithoutMileageCount: "avg",
      transactionsWithoutSupportingDocumentCount: "avg",
      transactionsCount: "sum",
    },
  };
  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const findKeysToTranslate = useCallback(
    (key: keyof ManagerDataDisplayRow, value: string) => {
      switch (key) {
        case "role":
          return t(`users.management.role.${value}`);
        case "status":
          return t(`users.management.status.${value}`);
        case "requestedAt":
          return dateFormatterDayMonthYearLong2Digits(new Date(value));
        default:
          return value;
      }
    },
    [t],
  );

  const keysToTranslate = useMemo(
    () => ["requestedAt", "status", "role"] as (keyof ManagerDataDisplayRow)[],
    [],
  );

  useEffect(() => {
    if (company && addCompanyToCompaniesAccessStatus === "success") {
      triggerToastDS(t("users.addCompanyToCompaniesAccess.success") || "", "SUCCESS");
      void dispatch(UsersSlice.actions.resetAddCompanyToCompaniesAccessStatus());
      void dispatch(UsersSlice.actions.resetManagersListStatus());
    } else if (addCompanyToCompaniesAccessStatus === "failed") {
      triggerToastDS(t("users.addCompanyToCompaniesAccess.failure") || "", "ERROR");
      void dispatch(UsersSlice.actions.resetAddCompanyToCompaniesAccessStatus());
    }
  }, [company, addCompanyToCompaniesAccessStatus, dispatch, navigate, t])

  useEffect(() => {
    if (deactivateManagerStatus === "success") {
      triggerToast(t("users.management.deactivated.success") || "", "valid");
      void dispatch(UsersSlice.actions.resetDeactivatedManagerStatus());
    } else if (deactivateManagerStatus === "failed") {
      triggerToast(t("users.management.deactivated.failure") || "", "error");
      void dispatch(UsersSlice.actions.resetDeactivatedManagerStatus());
    }
  }, [dispatch, deactivateManagerStatus, t]);

  useEffect(() => {
    if (relaunchManagerStatus === "success") {
      triggerToast(t("users.management.relaunch.success") || "", "valid");
      void dispatch(UsersSlice.actions.resetDeactivatedManagerStatus());
    } else if (relaunchManagerStatus === "failed") {
      triggerToast(t("users.management.relaunch.failure") || "", "error");
      void dispatch(UsersSlice.actions.resetDeactivatedManagerStatus());
    }
  }, [dispatch, relaunchManagerStatus, t]);

  useEffect(() => {
    if (cancelPasswordCreationRequestStatus === "success") {
      triggerToast(t("users.management.cancel.success") || "", "valid");
      void dispatch(UsersSlice.actions.resetDeactivatedManagerStatus());
    } else if (cancelPasswordCreationRequestStatus === "failed") {
      triggerToast(t("users.management.cancel.failure") || "", "error");
      void dispatch(UsersSlice.actions.resetDeactivatedManagerStatus());
    }
  }, [dispatch, cancelPasswordCreationRequestStatus, t]);

  useEffect(() => {
    if (
      company &&
      managersListStatus === "idle" &&
      loadCompanyStatus === "success"
    ) {
      void dispatch(managersListAsync({ companyUuid: company.uuid }));
    }
  }, [dispatch, company, managersListStatus, loadCompanyStatus]);

  useEffect(() => {
    if (company && updateUserRole === "success") {
      void dispatch(managersListAsync({ companyUuid: company.uuid }));
    }
  }, [company, dispatch, updateUserRole]);

  const managersList: ManagerDataDisplayRow[] = useMemo(() => {
    const rowsWithId = managersListInit.map((av) => {
      let newDict: ManagerDataDisplayRow & { id: string } = {
        ...av,
        id: av.uuid,
        companiesCount: av.companiesAccess?.length ?? null,
        departmentsCount: av.departmentsAccess?.length ?? null,
        receiveMileageGapReports: av.receiveMileageGapReports
          ? t("users.management.column.receiveMileageGapReports.true")
          : t("users.management.column.receiveMileageGapReports.false"),
      };
      Object.entries(av).forEach(([key, value]) => {
        if (keysToTranslate.includes(key as keyof ManagerDataDisplay)) {
          newDict = {
            ...newDict,
            [key]: value
              ? findKeysToTranslate(
                  key as keyof ManagerDataDisplay,
                  value as string,
                )
              : "",
          };
        }
      });
      return newDict;
    });
    return sortByDateTime(rowsWithId, "desc");
  }, [managersListInit, t, findKeysToTranslate, keysToTranslate]);

  useEffect(() => {
    if (managersList && managersListStatus === "success") {
      const managersDateTimeSorted = sortByDateTime(managersList, "desc");
      setListDisplayed(managersDateTimeSorted);
    }
  }, [dispatch, managersList, managersListStatus]);

  const searchBarProps = {
    values: managersList,
    setFilterValues: setListDisplayed,
    keysToIgnore: [
      "uuid" as const,
      "email" as const,
      "phone" as const,
      "companiesAccess" as const,
      "companyGroup" as const,
    ],
  };

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("users.management.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  const onUpdateReceiveMileageGapReports = useCallback((uuid: string, current: boolean) => {
    void dispatch(updateUserMileageGapReportsAsync({
      userUuid: uuid,
      receiveMileageGapReports: !current,
    }));
  }, [dispatch]);

  const columnsDisplayed: {
    [index in keyof ManagerDataDisplay]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      name: {
        type: "string",
        width: 250,
        valueFormatter: (value: string, row) => {
          const firstName = (row["firstName"] as string)
            ? (row["firstName"] as string)[0]
            : "";
          const lastName = (row["lastName"] as string)
            ? (row["lastName"] as string)[0]
            : "";
          return (
            value && (
              <StyledRow>
                <Avatar
                  size={"S"}
                  color={"green"}
                  firstName={firstName}
                  lastName={lastName}
                />
                <Spacer x={0.5} />
                {value}
              </StyledRow>
            )
          );
        },
      },
      status: {
        type: "string",
        valueFormatter: (value: string) => {
          return (
            <StyledRow>
              <StatusColorDict status={value} />
            </StyledRow>
          );
        },
      },
      role: {
        type: "string",
        width: 200,
        valueFormatter: (value: string) =>
          value && <TextCapitalized>{value}</TextCapitalized>,
      },
      job: {
        type: "string",
        width: 250,
      },
      receiveMileageGapReports: {
        type: "string",
        width: 250,
        valueFormatter: (value: string, row: GridValidRowModel) => {
          if (!row["uuid"]) return null;
          const isActiveManager = row["status"] === t("users.management.status.ACTIVE")
          const checked = value === t("users.management.column.receiveMileageGapReports.true");
          return (
            <StyledRow>
              <div onClick={(e) => e.stopPropagation()}>
                <Switch
                  checked={checked}
                  onClick={() => onUpdateReceiveMileageGapReports(
                    row["uuid"] as string,
                    checked,
                  )}
                  size={"S"}
                  disabled={!isActiveManager}
                />
              </div>
            </StyledRow>
          );
        },
      },
      requestedAt: {
        type: "string",
      },
      companiesCount: {
        type: "number",
        width: 140,
      },
      departmentsCount: {
        type: "number",
        width: 140,
      }
    };
  }, [onUpdateReceiveMileageGapReports, t]);

  const sidePanelAction = useCallback(
    (props: Partial<ManagerDataDisplayRow>) => {
      navigate(`/home/users/management/${props.uuid}`);
    },
    [navigate],
  );
  
  if (params.tab !== "management" || !currentManager) {
    return;
  }
  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  return (
    <>
      <MaterialUiTable<keyof ManagerDataDisplay, ManagerDataDisplay>
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
        downloadViewAction={downloadViewAction}
        onRowClickAction={sidePanelAction}
      />
      <UserManagementSidePanel />
    </>
  );
};

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;
